import { vec2 } from "gl-matrix"
import { clamp, dist } from "@/utils/math"
import { HealingItem, Mask, Rectangle } from "@/core/types"
import {
	BrushStroke,
	calcBoundingBox,
	getClampedRects,
	getPaddedAndClampedRect,
} from "@/core/utils/brushing"
import { FlowResult } from "../detection/optical-flow"
import { logImageAlignment } from "../utils"
import { ArrayData, computeGaussianKernel, sobel2x2 } from "@/lib/ai/alignment/ai-line-detection"
import { getCoordsRange, getMean } from "@/lib/ai/calibration/ai-calibration-utils"

export interface AlignInfo {
	orientedSize: vec2
	initialOrientation: number
	dScene?: number
	flowResult: FlowResult
}

/**
 * Transform coordinate from unoriented space to oriented space
 * Convention: positive orientation = clockwise rotation, negative = counter-clockwise
 * @param coord - Coordinate in unoriented space [0,1]
 * @param initialOrientation - Number of 90° CW rotations (can be negative for CCW)
 * @returns Coordinate in oriented space
 */
export function toInitialOrientation(coord: vec2, initialOrientation: number): vec2 {
	const steps = ((initialOrientation % 4) + 4) % 4
	switch (steps) {
		case 0:
			return coord // 0°: no rotation
		case 1:
			return [1 - coord[1], coord[0]] // 90° CW: (x,y) → (1-y, x)
		case 2:
			return [1 - coord[0], 1 - coord[1]] // 180°: (x,y) → (1-x, 1-y)
		case 3:
			return [coord[1], 1 - coord[0]] // 270° CW (= -1, 90° CCW): (x,y) → (y, 1-x)
		default:
			return coord
	}
}

/**
 * Transform coordinate from oriented space back to unoriented space
 * This is the inverse of toInitialOrientation
 */
export function toNoOrientation(coord: vec2, initialOrientation: number): vec2 {
	const steps = ((initialOrientation % 4) + 4) % 4
	switch (steps) {
		case 0:
			return coord // 0°: no rotation
		case 1:
			return [coord[1], 1 - coord[0]] // 90° CCW (inverse of CW): (x,y) → (y, 1-x)
		case 2:
			return [1 - coord[0], 1 - coord[1]] // 180°: (x,y) → (1-x, 1-y)
		case 3:
			return [1 - coord[1], coord[0]] // 270° CCW (inverse of 270° CW): (x,y) → (1-y, x)
		default:
			return coord
	}
}

/**
 * Transform rectangle from unoriented space to oriented space
 * @param rect - Rectangle in unoriented space
 * @param initialOrientation - Number of 90° CW rotations (can be negative for CCW)
 * @returns Rectangle in oriented space
 */
export function rectToInitialOrientation(rect: Rectangle, initialOrientation: number): Rectangle {
	const center: vec2 = [rect.x + rect.width / 2, rect.y + rect.height / 2]
	const newCenter = toInitialOrientation(center, initialOrientation)

	// Swap width/height for 90° and 270° rotations
	const steps = ((initialOrientation % 4) + 4) % 4
	const needsSwap = steps === 1 || steps === 3
	const width = needsSwap ? rect.height : rect.width
	const height = needsSwap ? rect.width : rect.height

	return { x: newCenter[0] - width / 2, y: newCenter[1] - height / 2, width, height }
}

export function rectsToInitialOrientationAndShrink(
	srcRect: Rectangle,
	destRect: Rectangle,
	initialOrientation: number,
	shrink = 0.0025,
) {
	let rect1 = rectToInitialOrientation(srcRect, initialOrientation)
	let rect2 = rectToInitialOrientation(destRect, initialOrientation)

	if (shrink) {
		const [r1, b1] = [rect1.x + rect1.width, rect1.y + rect1.height]
		const [r2, b2] = [rect2.x + rect2.width, rect2.y + rect2.height]

		const dx = rect1.x === 0 || rect2.x === 0 ? 0 : shrink
		const dx2 = r1 === 1 || r2 === 1 ? 0 : -shrink
		const dy = rect1.y === 0 || rect2.y === 0 ? 0 : shrink
		const dy2 = b1 === 1 || b2 === 1 ? 0 : -shrink

		const dw = dx2 - dx
		const dh = dy2 - dy

		rect1 = {
			x: rect1.x + dx,
			y: rect1.y + dy,
			width: rect1.width + dw,
			height: rect1.height + dh,
		}
		rect2 = {
			x: rect2.x + dx,
			y: rect2.y + dy,
			width: rect2.width + dw,
			height: rect2.height + dh,
		}
	}

	return { rect1, rect2 }
}

/**
 * Transform rectangle from oriented space back to unoriented space
 * This is the inverse of rectToInitialOrientation
 */
export function rectToNoOrientation(
	rect: Rectangle,
	initialOrientation: number,
	padding = 0.0,
): Rectangle {
	const center: vec2 = [rect.x + rect.width / 2, rect.y + rect.height / 2]
	const newCenter = toNoOrientation(center, initialOrientation)

	// Swap width/height for 90° and 270° rotations
	const steps = ((initialOrientation % 4) + 4) % 4
	const needsSwap = steps === 1 || steps === 3
	let width = needsSwap ? rect.height : rect.width
	let height = needsSwap ? rect.width : rect.height

	if (padding) {
		width += padding * 2
		height += padding * 2
	}
	return { x: newCenter[0] - width / 2, y: newCenter[1] - height / 2, width, height }
}

function transformToUncropped(coord: vec2, alignInfo: AlignInfo): vec2 {
	const { initialOrientation } = alignInfo
	const [w, h] = [alignInfo.flowResult.width, alignInfo.flowResult.height]
	const [ow, oh] = alignInfo.flowResult.isTransposed ? [h, w] : [w, h]

	const outCoord: vec2 = [coord[0] / ow, coord[1] / oh]

	return toNoOrientation(outCoord, initialOrientation)
}

function transformFromUncropped(coord: vec2, alignInfo: AlignInfo): vec2 {
	const { initialOrientation } = alignInfo
	const [w, h] = [alignInfo.flowResult.width, alignInfo.flowResult.height]
	const [ow, oh] = alignInfo.flowResult.isTransposed ? [h, w] : [w, h]

	const [x, y] = toInitialOrientation(coord, initialOrientation)

	return [x * ow, y * oh]
}

function applyFlow(coord: vec2, flowResult: FlowResult): vec2 {
	const w = flowResult.width
	const h = flowResult.height
	const [ow, oh] = flowResult.isTransposed ? [h, w] : [w, h]

	const x = clamp(Math.round(coord[0]), 0, ow - 1)
	const y = clamp(Math.round(coord[1]), 0, oh - 1)

	const index = flowResult.isTransposed ? x * w + y : y * w + x
	const dx = flowResult.flow[index * 2] as number
	const dy = flowResult.flow[index * 2 + 1] as number

	return flowResult.isTransposed ? [coord[0] + dy, coord[1] + dx] : [coord[0] + dx, coord[1] + dy]
}

/**
 * Compute alignment transform from optical flow by analyzing edge regions.
 * Center region is excluded since it was modified by AI.
 * Returns similarity transform (translation + rotation + scale) to align AI result with original.
 */
export function computeAlignmentFromFlow(
	flowResult: FlowResult,
	edgeMarginRatio = 0.2, // Use outer 20% on each side as edge region
): {
	dx: number
	dy: number
	scaleX: number
	scaleY: number
	confidence: number
} {
	const w = flowResult.width
	const h = flowResult.height
	const [ow, oh] = flowResult.isTransposed ? [h, w] : [w, h]

	// Compute gradient magnitude from gray image if available
	let gradientMap: number[] | null = null
	if (flowResult.grayImage?.data) {
		gradientMap = sobel2x2(flowResult.grayImage).total
	}

	// Define edge regions (exclude center to avoid AI-modified content)
	const marginW = Math.floor(ow * edgeMarginRatio)
	const marginH = Math.floor(oh * edgeMarginRatio)
	const centerLeft = marginW
	const centerRight = ow - marginW
	const centerTop = marginH
	const centerBottom = oh - marginH
	const gradientThreshold = 4

	// Use median for robustness (less sensitive to outliers than mean)
	const median = (arr: number[]) => {
		const sorted = [...arr].sort((a, b) => a - b)
		const mid = Math.floor(sorted.length / 2)
		return sorted.length % 2 === 0
			? ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2
			: (sorted[mid] ?? 0)
	}

	// Separable robust search for anisotropic scales (scaleX, scaleY) in [0.95, 1.05]
	const cx = ow / 2
	const cy = oh / 2

	// Prepare samples and observed displacements once
	const sampleXs: number[] = []
	const sampleYs: number[] = []
	const obsDx: number[] = []
	const obsDy: number[] = []
	for (let y = 0; y < oh; y += 1) {
		for (let x = 0; x < ow; x += 1) {
			if (x >= centerLeft && x < centerRight && y >= centerTop && y < centerBottom) continue
			if (gradientMap) {
				const gradIndex = y * ow + x
				const gradient = gradientMap[gradIndex] ?? 0
				if (gradient < gradientThreshold) continue
			}
			const index = flowResult.isTransposed ? x * w + y : y * w + x
			const flowDx = flowResult.flow[index * 2] as number
			const flowDy = flowResult.flow[index * 2 + 1] as number
			const dxObs = flowResult.isTransposed ? flowDy : flowDx
			const dyObs = flowResult.isTransposed ? flowDx : flowDy

			sampleXs.push(x)
			sampleYs.push(y)
			obsDx.push(dxObs)
			obsDy.push(dyObs)
		}
	}

	let bestScaleX = 1
	let bestScaleY = 1
	let bestTx = 0
	let bestTy = 0
	let bestCostX = Number.POSITIVE_INFINITY
	let bestCostY = Number.POSITIVE_INFINITY

	const sMin = 0.95
	const sMax = 1.05
	const sStep = 0.005

	// X-axis scale
	for (let s = sMin; s <= sMax + 1e-8; s += sStep) {
		const residDx: number[] = []
		for (let i = 0; i < sampleXs.length; i++) {
			const x = sampleXs[i] as number
			const sx = (x - cx) * (s - 1)
			residDx.push((obsDx[i] as number) - sx)
		}
		const tx = median(residDx)
		const madX = median(residDx.map((v) => Math.abs(v - tx)))
		if (madX < bestCostX) {
			bestCostX = madX
			bestScaleX = s
			bestTx = tx
		}
	}

	// Y-axis scale
	for (let s = sMin; s <= sMax + 1e-8; s += sStep) {
		const residDy: number[] = []
		for (let i = 0; i < sampleYs.length; i++) {
			const y = sampleYs[i] as number
			const sy = (y - cy) * (s - 1) // (y - cy) * s + cy - y
			residDy.push((obsDy[i] as number) - sy)
		}
		const ty = median(residDy)
		const madY = median(residDy.map((v) => Math.abs(v - ty)))
		if (madY < bestCostY) {
			bestCostY = madY
			bestScaleY = s
			bestTy = ty
		}
	}

	// Normalize translation by flow resolution for scale-independent storage
	const dxNorm = bestTx / ow
	const dyNorm = bestTy / oh

	// Confidence from combined MADs (lower is better)
	const combinedCost = (bestCostX + bestCostY) / 2
	const confidence = Math.max(0, Math.min(1, 1 - combinedCost / 3))

	logImageAlignment(
		`📊 Alignment (anisotropic): sX=${bestScaleX.toFixed(4)}, sY=${bestScaleY.toFixed(
			4,
		)}, tx=${bestTx.toFixed(2)}px (${(dxNorm * 100).toFixed(2)}%), ty=${bestTy.toFixed(
			2,
		)}px (${(dyNorm * 100).toFixed(2)}%), MADx=${bestCostX.toFixed(
			2,
		)}, MADy=${bestCostY.toFixed(2)}, samples=${sampleXs.length}`,
	)

	const result1 = {
		dx: dxNorm,
		dy: dyNorm,
		confidence,
		scaleX: bestScaleX,
		scaleY: bestScaleY,
	}

	const result2 = computeAlignmentFromFlowRobust(flowResult)

	logImageAlignment("result1", result1)
	logImageAlignment("result2", result2)
	return result2
}

export function computeAlignmentFromFlowRobust(
	flowResult: FlowResult,
	options?: {
		edgeMarginRatio?: number
		maxIterations?: number
		gradientThreshold?: number
		useRansacSeed?: boolean
		ridgeLambda?: number // ridge penalty on slope (shrinks scale toward 1)
		clampEps?: number // post-fit clamp around 1 for scale
	},
): {
	dx: number
	dy: number
	scaleX: number
	scaleY: number
	confidence: number
} {
	const w = flowResult.width
	const h = flowResult.height
	const [ow, oh] = flowResult.isTransposed ? [h, w] : [w, h]

	// config
	const maxIterations = options?.maxIterations ?? 5
	const gradientThreshold = options?.gradientThreshold ?? 3
	const useRansacSeed = options?.useRansacSeed ?? false
	const edgeMarginRatio = options?.edgeMarginRatio ?? 0.2
	const ridgeLambda = options?.ridgeLambda ?? 1e-4
	const clampEps = options?.clampEps ?? 0.05

	// Compute gradient magnitude from gray image if available
	let gradientMap: number[] | null = null
	if (flowResult.grayImage?.data) {
		gradientMap = sobel2x2(flowResult.grayImage).total
	}

	// Define edge regions (exclude center to avoid AI-modified content)
	const marginW = Math.floor(ow * edgeMarginRatio)
	const marginH = Math.floor(oh * edgeMarginRatio)
	const centerLeft = marginW
	const centerRight = ow - marginW
	const centerTop = marginH
	const centerBottom = oh - marginH

	// Prepare samples
	const sampleXs: number[] = []
	const sampleYs: number[] = []
	const obsDx: number[] = []
	const obsDy: number[] = []
	const wText: number[] = [] // optional texture weights from gradient
	for (let y = 0; y < oh; y++) {
		for (let x = 0; x < ow; x++) {
			if (x >= centerLeft && x < centerRight && y >= centerTop && y < centerBottom) continue
			if (gradientMap) {
				const gradIndex = y * ow + x
				const gradient = gradientMap[gradIndex] ?? 0
				if (gradient < gradientThreshold) continue
				wText.push(Math.min(gradient, 20))
			} else {
				wText.push(1)
			}
			const index = flowResult.isTransposed ? x * w + y : y * w + x
			const flowDx = flowResult.flow[index * 2] as number
			const flowDy = flowResult.flow[index * 2 + 1] as number
			const dxObs = flowResult.isTransposed ? flowDy : flowDx
			const dyObs = flowResult.isTransposed ? flowDx : flowDy

			sampleXs.push(x)
			sampleYs.push(y)
			obsDx.push(dxObs)
			obsDy.push(dyObs)
		}
	}

	// logImageAlignment("flowResult and gradientMap", { gradientMap, flowResult })
	// logImageAlignment("sampleXs", { sampleXs, sampleYs, obsDx, obsDy, wText })

	const cx = ow / 2
	const cy = oh / 2

	// Helpers
	const median = (arr: number[]) => {
		if (arr.length === 0) return 0
		const s = [...arr].sort((a, b) => a - b)
		const m = Math.floor(s.length / 2)
		return s.length % 2 === 0 ? ((s[m - 1] ?? 0) + (s[m] ?? 0)) / 2 : (s[m] ?? 0)
	}

	function weightedLeastSquares(r: number[], y: number[], w: number[], lambdaSlope: number) {
		let Sw = 0,
			Swr = 0,
			Swy = 0,
			Swrr = 0,
			Swry = 0
		for (let i = 0; i < r.length; i++) {
			const wi = w[i] ?? 1
			const ri = r[i] ?? 0
			const yi = y[i] ?? 0
			Sw += wi
			Swr += wi * ri
			Swy += wi * yi
			Swrr += wi * ri * ri
			Swry += wi * ri * yi
		}
		// Ridge on slope only: add lambda to slope diagonal term
		const denom = Sw * (Swrr + lambdaSlope) - Swr * Swr || 1e-6
		const a = (Sw * Swry - Swr * Swy) / denom // slope
		const t = (Swy - a * Swr) / (Sw || 1e-6) // intercept
		return { a, t }
	}

	// IRLS = Iteratively Reweighted Least Squares.
	// Idea: Solve a weighted least-squares fit repeatedly, updating point weights from residuals so outliers get down‑weighted.
	function irlsFit(r: number[], y: number[], baseWeights: number[], maxTranslation: number) {
		let w = baseWeights.map((v) => Math.max(v, 1e-3))
		let a = 0,
			t = 0
		for (let iter = 0; iter < maxIterations; iter++) {
			// Ridge WLS to get an unconstrained slope/intercept
			const ls = weightedLeastSquares(r, y, w, ridgeLambda)
			// Clamp slope via scale clamp so residuals respect the constraint
			const scaleUnclamped = 1 + ls.a
			const scaleClamped = Math.max(1 - clampEps, Math.min(1 + clampEps, scaleUnclamped))
			a = scaleClamped - 1
			// Recompute intercept with fixed slope using current weights
			let Sw = 0,
				Swr = 0,
				Swy = 0
			for (let i = 0; i < r.length; i++) {
				const wi = w[i] ?? 1
				const ri = r[i] ?? 0
				const yi = y[i] ?? 0
				Sw += wi
				Swr += wi * ri
				Swy += wi * yi
			}
			t = (Swy - a * Swr) / (Sw || 1e-6)
			// Clamp translation to ±maxTranslation (in pixels)
			t = clamp(t, -maxTranslation, maxTranslation)

			// Residuals under the constrained model drive robust weights
			const res = y.map((yi, i) => yi - (a * (r[i] ?? 0) + t))
			const med = median(res)
			const mad = median(res.map((v) => Math.abs(v - med))) || 1e-6
			const c = 2.5 * mad
			w = res.map((e, i) => {
				const z = Math.abs(e - med) / c
				const huber = z <= 1 ? 1 : 1 / z
				return huber * Math.max(baseWeights[i] ?? 1, 1e-3)
			})
			// logImageAlignment("iteration", iter, { a, t, w })
		}
		return { a, t, weights: w }
	}

	// dx_obs ≈ (scaleX − 1) · (x − cx) + tx
	// dy_obs ≈ (scaleY − 1) · (y − cy) + ty

	// Axis X: rX = x - cx, yX = dx_obs
	const rX = sampleXs.map((x) => x - cx)
	const yX = obsDx
	// Axis Y: rY = y - cy, yY = dy_obs
	const rY = sampleYs.map((y) => y - cy)
	const yY = obsDy

	const seedAndFit = (r: number[], y: number[]) => {
		// optional: simple RANSAC seed (2-point) for slope/intercept
		if (useRansacSeed && r.length >= 2) {
			let best = { a: 0, t: 0, inliers: 0 }
			const n = Math.min(200, r.length)
			for (let k = 0; k < n; k++) {
				const i = Math.floor(Math.random() * r.length)
				let j = Math.floor(Math.random() * r.length)
				if (j === i) j = (j + 1) % r.length
				const ri = r[i] ?? 0,
					rj = r[j] ?? 0
				const yi = y[i] ?? 0,
					yj = y[j] ?? 0
				const denom = rj - ri
				const a = Math.abs(denom) < 1e-6 ? 0 : (yj - yi) / denom
				const t = yi - a * ri
				const res = y.map((yk, m) => Math.abs((yk ?? 0) - (a * (r[m] ?? 0) + t)))
				const med = median(res)
				const mad = median(res.map((v) => Math.abs(v - med))) || 1e-6
				const c = 2.5 * mad
				const inliers = res.reduce((acc, v) => acc + (v <= c ? 1 : 0), 0)
				if (inliers > best.inliers) best = { a, t, inliers }
			}
			// We could bias the first IRLS iteration with best.{a,t} by adjusting weights,
			// but the IRLS below empirically converges well without explicit seeding.
		}
		// start IRLS with translation constraint (10% of image dimension in pixels)
		const maxTranslation = 0.1 * Math.max(ow, oh)
		const baseW = wText.length === r.length ? wText.slice() : new Array(r.length).fill(1)
		const { a, t, weights } = irlsFit(r, y, baseW, maxTranslation)
		return { a, t, weights }
	}

	const fitX = seedAndFit(rX, yX)
	const fitY = seedAndFit(rY, yY)

	const scaleX = 1 + fitX.a
	const scaleY = 1 + fitY.a
	const tx = fitX.t // pixels
	const ty = fitY.t

	const dx = tx / ow
	const dy = ty / oh

	// Confidence based on MAD-thresholded inlier ratios (independent from base weights)
	const resX = yX.map((yi, i) => yi - (fitX.a * (rX[i] ?? 0) + fitX.t))
	const medX = median(resX)
	const madX = median(resX.map((v) => Math.abs(v - medX))) || 1e-6
	const thrX = 2.5 * madX
	const inliersX = resX.reduce((acc, v) => acc + (Math.abs(v - medX) <= thrX ? 1 : 0), 0)
	const ratioX = resX.length > 0 ? inliersX / resX.length : 0

	const resY = yY.map((yi, i) => yi - (fitY.a * (rY[i] ?? 0) + fitY.t))
	const medY = median(resY)
	const madY = median(resY.map((v) => Math.abs(v - medY))) || 1e-6
	const thrY = 2.5 * madY
	const inliersY = resY.reduce((acc, v) => acc + (Math.abs(v - medY) <= thrY ? 1 : 0), 0)
	const ratioY = resY.length > 0 ? inliersY / resY.length : 0

	const confidence = Math.max(0, Math.min(1, (ratioX + ratioY) / 2))

	// Filter out low confidence alignments especially for large transformations
	const maxShift = Math.max(Math.abs(dx), Math.abs(dy))
	const maxScale = Math.max(Math.abs(scaleX - 1), Math.abs(scaleY - 1))
	const confidenceThreshold = 0.75 + Math.max(maxShift, maxScale) * 0.5
	if (confidence < confidenceThreshold && (maxShift > 0.06 || maxScale > 0.06)) {
		logImageAlignment("Low alignment confidence", { confidence, dx, dy, scaleX, scaleY })
		return { dx: 0, dy: 0, scaleX: 1, scaleY: 1, confidence }
	} else if (confidence < 0.4 && (maxShift > 0.02 || maxScale > 0.02)) {
		logImageAlignment("Very low alignment confidence", { confidence, dx, dy, scaleX, scaleY })
		return { dx: 0, dy: 0, scaleX: 1, scaleY: 1, confidence }
	}
	return { dx, dy, scaleX, scaleY, confidence }
}

export function transformInUncroppedSpace(coord: vec2, alignInfo: AlignInfo): vec2 {
	const [x, y] = transformFromUncropped(coord, alignInfo)

	const outCoord: vec2 = applyFlow([x, y], alignInfo.flowResult)

	return transformToUncropped(outCoord, alignInfo)
}

export function transformStroke(stroke: BrushStroke, alignInfo: AlignInfo): BrushStroke | null {
	const pointsCount = stroke.points.length / 2
	if (pointsCount < 1) return { ...stroke }

	const newPoints: number[] = []
	for (let i = 0; i < stroke.points.length - 1; i += 2) {
		const coord: vec2 = [stroke.points[i] as number, stroke.points[i + 1] as number]
		const newCoord = transformInUncroppedSpace(coord, alignInfo)
		newPoints.push(...newCoord)
	}

	const aspectRatio = alignInfo.orientedSize[1] / alignInfo.orientedSize[0]
	const halfBrushSizeX = aspectRatio > 1 ? stroke.size * 0.5 : stroke.size * 0.5 * aspectRatio
	const halfBrushSizeY = aspectRatio > 1 ? (stroke.size * 0.5) / aspectRatio : stroke.size * 0.5

	const scales: number[] = []
	let scale = getTransformedSizeScale(
		[stroke.points[0] as number, stroke.points[1] as number],
		[halfBrushSizeX, halfBrushSizeY],
		alignInfo,
	)
	scales.push(scale)
	if (pointsCount > 1) {
		scale = getTransformedSizeScale(
			[
				stroke.points[2 * pointsCount - 2] as number,
				stroke.points[2 * pointsCount - 1] as number,
			],
			[halfBrushSizeX, halfBrushSizeY],
			alignInfo,
		)
		scales.push(scale)
	}
	if (pointsCount > 2) {
		scale = getTransformedSizeScale(
			[
				stroke.points[pointsCount - (pointsCount % 2)] as number,
				stroke.points[pointsCount - (pointsCount % 2) + 1] as number,
			],
			[halfBrushSizeX, halfBrushSizeY],
			alignInfo,
		)
		scales.push(scale)
	}
	logImageAlignment("size scales:", scales)

	const minScale = Math.min(...scales)
	const maxScale = Math.max(...scales)
	// invalid result, mostly likely the object is gone and flow is incorrect
	if (maxScale - minScale >= 0.8) return null

	scale = getMean(scales)

	const newSize = stroke.size * scale

	return { ...stroke, points: newPoints, size: newSize }
}

function getTransformedSizeScale(point: vec2, halfBrushSize: vec2, alignInfo: AlignInfo) {
	const [x, y] = point
	const [sx, sy] = halfBrushSize
	const v1: vec2 = [x - sx, y - sy]
	const v2: vec2 = [x + sx, y - sy]
	const v3: vec2 = [x + sx, y + sy]
	const v4: vec2 = [x - sx, y + sy]
	const length = 4 * sx + 4 * sy

	const newv1 = transformInUncroppedSpace(v1, alignInfo)
	const newv2 = transformInUncroppedSpace(v2, alignInfo)
	const newv3 = transformInUncroppedSpace(v3, alignInfo)
	const newv4 = transformInUncroppedSpace(v4, alignInfo)
	const newLen =
		dist(newv1[0], newv1[1], newv2[0], newv2[1]) +
		dist(newv2[0], newv2[1], newv3[0], newv3[1]) +
		dist(newv3[0], newv3[1], newv4[0], newv4[1]) +
		dist(newv4[0], newv4[1], newv1[0], newv1[1])

	const scale = newLen / length
	return scale
}

export function transformHealingMask(
	healingMask: HealingItem,
	alignInfo: AlignInfo,
): HealingItem | null {
	const [w, h] = alignInfo.orientedSize
	const hwRatio = alignInfo.initialOrientation % 2 ? w / h : h / w

	const result: HealingItem = { ...healingMask }

	const newStrokes = result.strokes
		.map((stroke) => transformStroke(stroke, alignInfo))
		.filter((val): val is NonNullable<typeof val> => val != null)
	if (newStrokes.length < result.strokes.length) return null

	result.strokes = newStrokes
	result.destinationCenter = transformInUncroppedSpace(result.destinationCenter, alignInfo)
	result.strokesRect = calcBoundingBox(result.strokes, hwRatio)

	const { x, y, width, height } = result.strokesRect
	const eps = 0.002
	if (x >= 1 - eps || y >= 1 - eps || x + width <= eps || y + height <= eps) {
		// dest out of image bounds
		return null
	}

	// move source while avoiding edge-clamping to smaller than dest size
	const destCenter = result.destinationCenter
	const destRect = getPaddedAndClampedRect(result.strokesRect)
	result.sourceCenter = transformInUncroppedSpace(result.sourceCenter, alignInfo)
	result.sourceCenter[0] = clamp(
		result.sourceCenter[0],
		destCenter[0] - destRect.x,
		1 - (destRect.x + destRect.width - destCenter[0]),
	)
	result.sourceCenter[1] = clamp(
		result.sourceCenter[1],
		destCenter[1] - destRect.y,
		1 - (destRect.y + destRect.height - destCenter[1]),
	)

	const { destinationRect, sourceRect } = getClampedRects({
		strokesRect: result.strokesRect,
		sourceCenter: result.sourceCenter,
		destinationCenter: result.destinationCenter,
	})

	result.destinationRect = destinationRect
	result.sourceRect = sourceRect

	return result
}

export function transformMasks(masks: Mask[], alignInfo: AlignInfo) {
	const newMasks: Mask[] = []

	for (const mask of masks) {
		let newMask: Mask | null = { ...mask }

		if (mask.type === "gradient") {
			const [sx, sy] = transformInUncroppedSpace([mask.startX, mask.startY], alignInfo)
			const [ex, ey] = transformInUncroppedSpace([mask.endX, mask.endY], alignInfo)

			newMask = { ...mask, startX: sx, startY: sy, endX: ex, endY: ey }
		} else if (mask.type === "radial") {
			const [cx, cy] = transformInUncroppedSpace([mask.centerX, mask.centerY], alignInfo)
			const scale = getTransformedSizeScale(
				[mask.centerX, mask.centerY],
				[mask.radiusX, mask.radiusY],
				alignInfo,
			)
			newMask = {
				...mask,
				centerX: cx,
				centerY: cy,
				radiusX: mask.radiusX * scale,
				radiusY: mask.radiusY * scale,
			}
		} else if (mask.type === "brush") {
			const newStrokes = mask.strokes
				.map((stroke) => transformStroke(stroke, alignInfo))
				.filter((val): val is NonNullable<typeof val> => val != null)
			if (newStrokes.length < mask.strokes.length) {
				newMask = null
			} else {
				newMask = { ...mask, strokes: newStrokes }
			}
		}

		logImageAlignment("align mask:", { mask, newMask })
		if (newMask) newMasks.push(newMask)
	}

	return newMasks
}

// Following are functions to detect FAST corner and compute BRIEF feature

export interface BriefFeature {
	coord: vec2
	feature: Uint32Array
}

export function getBriefFeature(grayImage: ArrayData, rect: Rectangle) {
	const smoothed = gaussianForRect(grayImage, rect)
	const corners = getFastCorners(smoothed, rect)
	const features = computeBriefFeature(smoothed, corners, rect)

	return features
}

// Only smooth rect+padding region
function gaussianForRect(grayImage: ArrayData, rect: Rectangle): ArrayData {
	const input = grayImage.data
	const width = grayImage.width
	const height = grayImage.height
	let { startX, endX, startY, endY } = getCoordsRange(rect, [width, height])
	const padding = 4
	startX -= padding
	startY -= padding
	endX += padding
	endY += padding

	const h = 3
	const gaussKernel = computeGaussianKernel(0.75, h)
	const gaussSize = 2 * h + 1

	const smoothed = input.slice()
	for (let y = startY; y < endY; y++) {
		for (let x = startX; x < endX; x++) {
			if (x < 3 || x >= width - 3 || y < 3 || y >= height - 3) {
				continue // skip image border pixels
			}
			let sum = 0
			for (let x1 = 0; x1 < gaussSize; x1++) {
				for (let y1 = 0; y1 < gaussSize; y1++) {
					const x2 = x - h + x1
					const y2 = y - h + y1
					const value =
						(input[y2 * width + x2] ?? 0) * <number>gaussKernel[y1 * gaussSize + x1]
					sum += value
				}
			}
			smoothed[y * width + x] = sum
		}
	}

	return { data: smoothed, width, height }
}

interface Corner {
	coord: vec2
	score: number
}

function getFastCorners(gray: ArrayData, rect: Rectangle) {
	const { startX, endX, startY, endY } = getCoordsRange(rect, [gray.width, gray.height])
	const pw = endX - startX
	const ph = endY - startY

	if (pw < 1 || ph < 1) return []

	const b = 5 // threshold, original 20
	const cornerIndices: number[] = new Array(pw * ph).fill(-1)

	const pixelOffset: vec2[] = [
		[0, 3],
		[1, 3],
		[2, 2],
		[3, 1],
		[3, 0],
		[3, -1],
		[2, -2],
		[1, -3],
		[0, -3],
		[-1, -3],
		[-2, -2],
		[-3, -1],
		[-3, 0],
		[-3, 1],
		[-2, 2],
		[-1, 3],
	]

	const corners: Corner[] = []
	const fastThresh = 2 // 3
	const thresh = 9
	let count = 0
	for (let y = startY; y < endY; y++) {
		for (let x = startX; x < endX; x++) {
			if (x < 3 || x >= gray.width - 3 || y < 3 || y >= gray.height - 3) {
				continue // skip border pixels
			}

			const centerPixel = gray.data[y * gray.width + x] as number
			const cb = centerPixel + b
			const c_b = centerPixel - b

			let greater = 0
			let lower = 0

			let posScore = 0
			let negScore = 0

			// First test for the quick rejection
			for (let i = 0; i < 16; i += 4) {
				const nx = x + (pixelOffset[i] as vec2)[0]
				const ny = y + (pixelOffset[i] as vec2)[1]
				const pixel_val = gray.data[ny * gray.width + nx] as number

				if (pixel_val > cb) {
					greater++
					posScore += pixel_val - cb
				}
				if (pixel_val < c_b) {
					lower++
					negScore += c_b - pixel_val
				}
			}

			if (Math.max(greater, lower) < fastThresh) {
				continue // Not enough evidence of a corner
			}

			// Full check
			for (let i = 0; i < 16; i++) {
				if (i % 4 === 0) continue
				const nx = x + (pixelOffset[i] as vec2)[0]
				const ny = y + (pixelOffset[i] as vec2)[1]
				const pixel_val = gray.data[ny * gray.width + nx] as number

				if (pixel_val > cb) {
					greater++
					posScore += pixel_val - cb
				}
				if (pixel_val < c_b) {
					lower++
					negScore += c_b - pixel_val
				}
			}

			if (Math.max(greater, lower) >= thresh) {
				// mark as corner
				const i = (y - startY) * pw + x - startX
				cornerIndices[i] = count
				corners.push({
					coord: [x - startX, y - startY],
					score: Math.max(posScore, negScore),
				})
				count++
			}
		}
	}

	// non-maximal suppression
	const radius = 1

	// index sort desc
	const indices = corners.map((_, index) => index)
	indices.sort((a, b) => (corners[b]?.score ?? 0) - (corners[a]?.score ?? 0))

	const keep = new Array(corners.length).fill(1)
	for (const i of indices) {
		if (keep[i] !== 1) continue

		const corner = corners[i] as Corner
		const [x, y] = corner.coord
		for (let i = -radius; i <= radius; i++) {
			for (let j = -radius; j <= radius; j++) {
				if (i === 0 && j === 0) continue
				const index2 = (y + i) * pw + x + j
				const neighborIndex = cornerIndices[index2] ?? -1
				if (neighborIndex >= 0) {
					// remove neighbor
					keep[neighborIndex] = 0
				}
			}
		}
	}

	const result: vec2[] = []
	for (let i = 0; i < keep.length; i++) {
		if (keep[i]) {
			result.push((corners[i] as Corner).coord)
		}
	}

	return result
}

function computeBriefFeature(gray: ArrayData, coords: vec2[], rect: Rectangle) {
	//Pre-defined random sampling patterns
	const points = [
		8, -3, 9, 5, 4, 2, 7, -12, -11, 9, -8, 2, 7, -12, 12, -13, 2, -13, 2, 12, 1, -7, 1, 6, -2,
		-10, -2, -4, -13, -13, -11, -8, -13, -3, -12, -9, 10, 4, 11, 9, -13, -8, -8, -9, -11, 7, -9,
		12, 7, 7, 12, 6, -4, -5, -3, 0, -13, 2, -12, -3, -9, 0, -7, 5, 12, -6, 12, -1, -3, 6, -2,
		12, -6, -13, -4, -8, 11, -13, 12, -8, 4, 7, 5, 1, 5, -3, 10, -3, 3, -7, 6, 12, -8, -7, -6,
		-2, -2, 11, -1, -10, -13, 12, -8, 10, -7, 3, -5, -3, -4, 2, -3, 7, -10, -12, -6, 11, 5, -12,
		6, -7, 5, -6, 7, -1, 1, 0, 4, -5, 9, 11, 11, -13, 4, 7, 4, 12, 2, -1, 4, 4, -4, -12, -2, 7,
		-8, -5, -7, -10, 4, 11, 9, 12, 0, -8, 1, -13, -13, -2, -8, 2, -3, -2, -2, 3, -6, 9, -4, -9,
		8, 12, 10, 7, 0, 9, 1, 3, 7, -5, 11, -10, -13, -6, -11, 0, 10, 7, 12, 1, -6, -3, -6, 12, 10,
		-9, 12, -4, -13, 8, -8, -12, -13, 0, -8, -4, 3, 3, 7, 8, 5, 7, 10, -7, -1, 7, 1, -12, 3,
		-10, 5, 6, 2, -4, 3, -10, -13, 0, -13, 5, -13, -7, -12, 12, -13, 3, -11, 8, -7, 12, -4, 7,
		6, -10, 12, 8, -9, -1, -7, -6, -2, -5, 0, 12, -12, 5, -7, 5, 3, -10, 8, -13, -7, -7, -4, 5,
		-3, -2, -1, -7, 2, 9, 5, -11, -11, -13, -5, -13, -1, 6, 0, -1, 5, -3, 5, 2, -4, -13, -4, 12,
		-9, -6, -9, 6, -12, -10, -8, -4, 10, 2, 12, -3, 7, 12, 12, 12, -7, -13, -6, 5, -4, 9, -3, 4,
		7, -1, 12, 2, -7, 6, -5, 1, -13, 11, -12, 5, -3, 7, -2, -6, 7, -8, 12, -7, -13, -7, -11,
		-12, 1, -3, 12, 12, 2, -6, 3, 0, -4, 3, -2, -13, -1, -13, 1, 9, 7, 1, 8, -6, 1, -1, 3, 12,
		9, 1, 12, 6, -1, -9, -1, 3, -13, -13, -10, 5, 7, 7, 10, 12, 12, -5, 12, 9, 6, 3, 7, 11, 5,
		-13, 6, 10, 2, -12, 2, 3, 3, 8, 4, -6, 2, 6, 12, -13, 9, -12, 10, 3, -8, 4, -7, 9, -11, 12,
		-4, -6, 1, 12, 2, -8, 6, -9, 7, -4, 2, 3, 3, -2, 6, 3, 11, 0, 3, -3, 8, -8, 7, 8, 9, 3, -11,
		-5, -6, -4, -10, 11, -5, 10, -5, -8, -3, 12, -10, 5, -9, 0, 8, -1, 12, -6, 4, -6, 6, -11,
		-10, 12, -8, 7, 4, -2, 6, 7, -2, 0, -2, 12, -5, -8, -5, 2, 7, -6, 10, 12, -9, -13, -8, -8,
		-5, -13, -5, -2, 8, -8, 9, -13, -9, -11, -9, 0, 1, -8, 1, -2, 7, -4, 9, 1, -2, 1, -1, -4,
		11, -6, 12, -11, -12, -9, -6, 4, 3, 7, 7, 12, 5, 5, 10, 8, 0, -4, 2, 8, -9, 12, -5, -13, 0,
		7, 2, 12, -1, 2, 1, 7, 5, 11, 7, -9, 3, 5, 6, -8, -13, -4, -8, 9, -5, 9, -3, -3, -4, -7, -3,
		-12, 6, 5, 8, 0, -7, 6, -6, 12, -13, 6, -5, -2, 1, -10, 3, 10, 4, 1, 8, -4, -2, -2, 2, -13,
		2, -12, 12, 12, -2, -13, 0, -6, 4, 1, 9, 3, -6, -10, -3, -5, -3, -13, -1, 1, 7, 5, 12, -11,
		4, -2, 5, -7, -13, 9, -9, -5, 7, 1, 8, 6, 7, -8, 7, 6, -7, -4, -7, 1, -8, 11, -7, -8, -13,
		6, -12, -8, 2, 4, 3, 9, 10, -5, 12, 3, -6, -5, -6, 7, 8, -3, 9, -8, 2, -12, 2, 8, -11, -2,
		-10, 3, -12, -13, -7, -9, -11, 0, -10, -5, 5, -3, 11, 8, -2, -13, -1, 12, -1, -8, 0, 9, -13,
		-11, -12, -5, -10, -2, -10, 11, -3, 9, -2, -13, 2, -3, 3, 2, -9, -13, -4, 0, -4, 6, -3, -10,
		-4, 12, -2, -7, -6, -11, -4, 9, 6, -3, 6, 11, -13, 11, -5, 5, 11, 11, 12, 6, 7, -5, 12, -2,
		-1, 12, 0, 7, -4, -8, -3, -2, -7, 1, -6, 7, -13, -12, -8, -13, -7, -2, -6, -8, -8, 5, -6,
		-9, -5, -1, -4, 5, -13, 7, -8, 10, 1, 5, 5, -13, 1, 0, 10, -13, 9, 12, 10, -1, 5, -8, 10,
		-9, -1, 11, 1, -13, -9, -3, -6, 2, -1, -10, 1, 12, -13, 1, -8, -10, 8, -11, 10, -6, 2, -13,
		3, -6, 7, -13, 12, -9, -10, -10, -5, -7, -10, -8, -8, -13, 4, -6, 8, 5, 3, 12, 8, -13, -4,
		2, -3, -3, 5, -13, 10, -12, 4, -13, 5, -1, -9, 9, -4, 3, 0, 3, 3, -9, -12, 1, -6, 1, 3, 2,
		4, -8, -10, -10, -10, 9, 8, -13, 12, 12, -8, -12, -6, -5, 2, 2, 3, 7, 10, 6, 11, -8, 6, 8,
		8, -12, -7, 10, -6, 5, -3, -9, -3, 9, -1, -13, -1, 5, -3, -7, -3, 4, -8, -2, -8, 3, 4, 2,
		12, 12, 2, -5, 3, 11, 6, -9, 11, -13, 3, -1, 7, 12, 11, -1, 12, 4, -3, 0, -3, 6, 4, -11, 4,
		12, 2, -4, 2, 1, -10, -6, -8, 1, -13, 7, -11, 1, -13, 12, -11, -13, 6, 0, 11, -13, 0, -1, 1,
		4, -13, 3, -9, -2, -9, 8, -6, -3, -13, -6, -8, -2, 5, -9, 8, 10, 2, 7, 3, -9, -1, -6, -1,
		-1, 9, 5, 11, -2, 11, -3, 12, -8, 3, 0, 3, 5, -1, 4, 0, 10, 3, -6, 4, 5, -13, 0, -10, 5, 5,
		8, 12, 11, 8, 9, 9, -6, 7, -4, 8, -12, -10, 4, -10, 9, 7, 3, 12, 4, 9, -7, 10, -2, 7, 0, 12,
		-2, -1, -6, 0, -11,
	] // length=1024

	const { data, width, height } = gray
	const { startX, endX, startY, endY } = getCoordsRange(rect, [width, height])
	const pw = endX - startX
	const ph = endY - startY

	const result: BriefFeature[] = []
	for (const coord of coords) {
		let [x, y] = coord
		x += startX
		y += startY

		const feature = new Uint32Array(8)

		for (let ii = 0; ii < 256; ii++) {
			const x1 = x + (points[ii * 4 + 1] as number)
			const y1 = y + (points[ii * 4] as number)
			const x2 = x + (points[ii * 4 + 3] as number)
			const y2 = y + (points[ii * 4 + 2] as number)

			const idx1 = y1 * width + x1
			const idx2 = y2 * width + x2

			const pts1 = data[idx1] ?? 0
			const pts2 = data[idx2] ?? 0

			const bitPosition = ii % 32
			const arrayIndex = Math.floor(ii / 32)

			if (pts1 < pts2 && arrayIndex < feature.length) {
				feature[arrayIndex] = (feature[arrayIndex] ?? 0) | (1 << bitPosition)
			}
		}

		result.push({ coord: [coord[0] / pw, coord[1] / ph], feature })
	}

	return result
}

export function hammingDistance(descriptor1: Uint32Array, descriptor2: Uint32Array): number {
	let dist = 0

	for (let i = 0; i < descriptor1.length; i++) {
		const d1 = descriptor1[i]
		const d2 = descriptor2[i]
		if (d1 == undefined || d2 == undefined) continue
		const diff = d1 ^ d2 // XOR to find differing bits
		dist += popcount(diff) // Count number of 1s in the binary representation of diff
	}

	return dist
}

function popcount(x: number): number {
	let count = 0
	while (x !== 0) {
		count += x & 1 // Increase count if the lowest bit is 1
		x >>>= 1 // Shift right by one bit to check the next bit
	}
	return count
}

export function isMatched(features1: BriefFeature[], features2: BriefFeature[]) {
	if (features1.length === 0 || features2.length === 0) return false

	const aveFeatureCount = (features1.length + features2.length) / 2

	const shiftThresh = 0.15

	let hammingThresh = 100
	let matchedPstSrcThresh = 0.25

	// If patches have clear/many features, they can match well with stricter condition
	if (features1.length >= 50) {
		hammingThresh = clamp(120 - features1.length * 0.4, 75, 100)
		matchedPstSrcThresh = clamp(features1.length / 200, 0.3, 0.65)
	}

	let matchedCount = 0
	let matchedPercent = 0
	let matchedPercentSrc = 0
	const indexMatched = new Array(features2.length).fill(0)
	for (let i = 0; i < features1.length; i++) {
		const f1 = features1[i] as BriefFeature
		let minD = 1000
		let matchedJ = -1
		for (let j = 0; j < features2.length; j++) {
			if (indexMatched[j] === 1) continue

			const f2 = features2[j] as BriefFeature
			const dx = f2.coord[0] - f1.coord[0]
			const dy = f2.coord[1] - f1.coord[1]
			if (Math.abs(dx) < shiftThresh && Math.abs(dy) < shiftThresh) {
				const d = hammingDistance(f1.feature, f2.feature)
				if (d < minD) {
					minD = d
					matchedJ = j
					if (minD <= hammingThresh) break
				}
			}
		}
		if (minD <= hammingThresh) {
			matchedCount++
			matchedPercent = matchedCount / aveFeatureCount
			matchedPercentSrc = matchedCount / features1.length
			indexMatched[matchedJ] = 1

			if (
				matchedPercentSrc >= matchedPstSrcThresh &&
				(matchedPercent >= 0.5 ||
					(matchedPercent >= 0.25 && matchedCount >= 8 && features1.length < 50))
			) {
				return true
			}
		}
	}

	logImageAlignment("Feature unmatched:", {
		matchedCount,
		matchedPercent,
		matchedPercentSrc,
		f1: features1.length,
		f2: features2.length,
	})

	return false
}
